import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  ensureSession,
  type AlertSuggestion,
  type Category,
  type Deal,
  type Hunt,
  type Me,
  type Radar,
} from './api';
import { DealTag } from './components/DealTag';
import { DealPanel } from './components/DealPanel';
import { RadarPanel } from './components/RadarPanel';
import { AlertPanel } from './components/AlertPanel';
import { Scope as ScopeMark, ScopeDial } from './components/Scope';

type Sort = 'new' | 'hot' | 'discount';
type Scope = 'near' | 'all';

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    // No sign-in: the session is provisioned on arrival, so the first thing a
    // visitor sees is deals.
    ensureSession()
      .then(setMe)
      .catch((e) => setBootError(e instanceof Error ? e.message : 'Could not reach the radar'));
  }, []);

  if (bootError) return <BootFailure message={bootError} />;
  if (!me) return null;
  return <Shell me={me} />;
}

/**
 * The one screen that replaces the old login: there is nothing for the visitor
 * to do but retry, so say that plainly rather than offering a form.
 */
function BootFailure({ message }: { message: string }) {
  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div style={{ display: 'grid', placeItems: 'center', marginBottom: 10 }}>
          <ScopeMark radiusM={16000} size={130} showLabels={false} />
        </div>
        <div className="brand" style={{ justifyContent: 'center', marginBottom: 4 }}>
          <span className="brand-mark">Price Fail</span>
          <span className="brand-sub">radar</span>
        </div>
        <p className="error" style={{ textAlign: 'center' }}>{message}</p>
        <button
          className="btn"
          style={{ width: '100%', marginTop: 12 }}
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function Shell({ me }: { me: Me }) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [radars, setRadars] = useState<Radar[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scope, setScope] = useState<Scope>('near');
  const [sort, setSort] = useState<Sort>('new');
  const [category, setCategory] = useState<string | undefined>();
  const [query, setQuery] = useState('');
  const [inStoreOnly, setInStoreOnly] = useState(false);

  const [openDeal, setOpenDeal] = useState<string | null>(null);
  const [editingRadar, setEditingRadar] = useState<Radar | null | 'new'>(null);
  const [pushState, setPushState] = useState<'idle' | 'on' | 'unsupported'>('idle');

  const [hunts, setHunts] = useState<Hunt[]>([]);
  const [suggestions, setSuggestions] = useState<AlertSuggestion[]>([]);
  // `null` closed, `'new'` blank, a suggestion pre-fills, a Hunt edits.
  const [editingAlert, setEditingAlert] = useState<Hunt | 'new' | AlertSuggestion | null>(null);

  const loadRadars = useCallback(() => {
    api.radars().then((r) => setRadars(r.radars)).catch(() => {});
  }, []);

  const loadHunts = useCallback(() => {
    api.hunts().then((r) => setHunts(r.hunts)).catch(() => {});
  }, []);

  useEffect(() => {
    loadRadars();
    loadHunts();
    api.taxonomy().then((r) => setCategories(r.categories)).catch(() => {});
    api.alertSuggestions().then((r) => setSuggestions(r.suggestions)).catch(() => {});
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) setPushState('unsupported');
  }, [loadRadars, loadHunts]);

  const loadDeals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.deals({
        scope,
        sort,
        category,
        q: query || undefined,
        inStore: inStoreOnly || undefined,
        limit: 60,
      });
      setDeals(r.deals);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load deals');
    } finally {
      setLoading(false);
    }
  }, [scope, sort, category, query, inStoreOnly]);

  useEffect(() => {
    // Debounced so typing in search doesn't fire a request per keystroke.
    const t = setTimeout(loadDeals, query ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadDeals, query]);

  // New deals land continuously; refresh on an interval so the feed stays live
  // without the user reaching for reload.
  useEffect(() => {
    const timer = setInterval(loadDeals, 60_000);
    return () => clearInterval(timer);
  }, [loadDeals]);

  const nearby = useMemo(() => deals.filter((d) => d.distanceM !== null).length, [deals]);

  // Don't offer a starter alert that is already running — a second "Cheap soda"
  // would just split its own notifications in two.
  const unusedSuggestions = useMemo(() => {
    const taken = new Set(hunts.map((h) => h.query.toLowerCase()));
    return suggestions.filter((s) => !taken.has(s.query.toLowerCase()));
  }, [suggestions, hunts]);

  async function enablePush() {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const reg = await navigator.serviceWorker.register('/sw.js');
      const existing = await reg.pushManager.getSubscription();
      const sub =
        existing ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          // The server hands out its VAPID public key at build/deploy time.
          applicationServerKey: import.meta.env.VITE_VAPID_PUBLIC_KEY,
        }));
      await api.registerWebPush(sub.toJSON());
      setPushState('on');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not turn on alerts');
    }
  }

  return (
    <div className="app">
      <nav className="rail">
        <div className="brand">
          <span className="brand-mark">Price Fail</span>
          <span className="brand-sub">radar</span>
        </div>

        <div>
          <div className="rail-heading">
            <span>Radars</span>
            <button onClick={() => setEditingRadar('new')}>+ Add</button>
          </div>
          {radars.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--field-text-dim)', margin: 0 }}>
              Drop a pin to start watching an area.
            </p>
          )}
          {radars.map((r) => (
            <button
              key={r.id}
              className={`radar${r.active ? '' : ' radar-off'}`}
              onClick={() => setEditingRadar(r)}
            >
              <ScopeDial radiusM={r.radiusM} active={r.active && r.notify} />
              <span>
                <span className="radar-name">{r.name}</span>
                <span className="radar-sub" style={{ display: 'block' }}>
                  {r.radiusDisplay}
                  {r.categories.length ? ` · ${r.categories.length} cats` : ''}
                  {r.notify ? '' : ' · muted'}
                </span>
              </span>
            </button>
          ))}
        </div>

        {/*
          Item alerts. A radar answers "where am I willing to go"; this answers
          "what am I actually looking for", and for a consumable that is the
          only question — nobody drives somewhere in the hope of soda, they want
          to be told when soda is cheap wherever it is.
        */}
        <div>
          <div className="rail-heading">
            <span>Alerts</span>
            <button onClick={() => setEditingAlert('new')}>+ Add</button>
          </div>

          {hunts.map((h) => (
            <button
              key={h.id}
              className={`radar${h.active && h.notify ? '' : ' radar-off'}`}
              onClick={() => setEditingAlert(h)}
            >
              <span className="alert-dot" aria-hidden />
              <span>
                <span className="radar-name">{h.name}</span>
                <span className="radar-sub" style={{ display: 'block' }}>
                  {alertSubtitle(h)}
                </span>
              </span>
            </button>
          ))}

          {/*
            One tap to a working alert. An empty screen with a text box is a
            worse product than it looks: the person knows they want cheap soda,
            not that the useful threshold is per-litre. A tapped suggestion
            arrives configured and the editor then shows exactly what it did.
          */}
          {unusedSuggestions.length > 0 && (
            <>
              <p className="alert-mini" style={{ margin: '10px 0 6px' }}>
                {hunts.length === 0 ? 'Try one of these' : 'Add another'}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {unusedSuggestions.slice(0, 6).map((s) => (
                  <button
                    key={s.id}
                    className="chip"
                    title={s.detail}
                    onClick={() => setEditingAlert(s)}
                  >
                    {s.emoji} {s.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div>
          <div className="rail-heading">
            <span>Delivery</span>
          </div>
          {pushState === 'unsupported' ? (
            <p style={{ fontSize: 12, color: 'var(--field-text-dim)', margin: 0 }}>
              This browser cannot receive push alerts. The iPhone app can.
            </p>
          ) : pushState === 'on' ? (
            <span className="live">Alerts on</span>
          ) : (
            <button className="btn btn-ghost" style={{ width: '100%' }} onClick={enablePush}>
              Turn on browser alerts
            </button>
          )}
        </div>

        <div style={{ marginTop: 'auto', paddingTop: 20, borderTop: '1px solid var(--field-line)' }}>
          <p style={{ fontSize: 13, margin: '0 0 2px', color: 'var(--field-text)' }}>
            {me.displayName}
          </p>
          {/*
            Worth saying once, quietly: with no account there is nothing holding
            these radars but this browser, and someone who clears it should not
            be surprised to find them gone.
          */}
          <p style={{ fontSize: 12, color: 'var(--field-text-dim)', margin: 0 }}>
            Saved in this browser. The iPhone app signs in with Apple to keep it.
          </p>
        </div>
      </nav>

      <main className="main">
        <div className="toolbar">
          <input
            className="search"
            placeholder="Search deals — item, brand, shop"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search deals"
          />
          <button
            className="chip"
            aria-pressed={scope === 'near'}
            onClick={() => setScope(scope === 'near' ? 'all' : 'near')}
          >
            In my radars
          </button>
          <button
            className="chip"
            aria-pressed={inStoreOnly}
            onClick={() => setInStoreOnly(!inStoreOnly)}
          >
            In store
          </button>
          {(['new', 'hot', 'discount'] as Sort[]).map((s) => (
            <button key={s} className="chip" aria-pressed={sort === s} onClick={() => setSort(s)}>
              {s === 'new' ? 'Newest' : s === 'hot' ? 'Hottest' : 'Biggest drop'}
            </button>
          ))}
        </div>

        <div className="toolbar" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 16 }}>
          <button className="chip" aria-pressed={!category} onClick={() => setCategory(undefined)}>
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              className="chip"
              aria-pressed={category === c.id}
              onClick={() => setCategory(category === c.id ? undefined : c.id)}
            >
              {c.emoji} {c.label}
            </button>
          ))}
        </div>

        <p
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            color: 'var(--field-text-dim)',
            letterSpacing: '0.06em',
            margin: '0 0 16px',
          }}
        >
          {loading ? 'Scanning…' : `${deals.length} deals`}
          {nearby > 0 && ` · ${nearby} within range`}
        </p>

        {error && <p className="error">{error}</p>}

        <div className="feed">
          {deals.map((d) => (
            <DealTag key={d.id} deal={d} onOpen={setOpenDeal} />
          ))}

          {!loading && deals.length === 0 && (
            <div className="empty">
              <strong>Nothing in range yet</strong>
              {scope === 'near'
                ? 'Widen a radar or switch to every deal to see what is out there.'
                : 'New deals land as the watched accounts post them.'}
            </div>
          )}
        </div>
      </main>

      {openDeal && (
        <DealPanel
          dealId={openDeal}
          onClose={() => setOpenDeal(null)}
          onVoted={(updated) =>
            setDeals((prev) => prev.map((d) => (d.id === updated.id ? updated : d)))
          }
        />
      )}

      {editingRadar !== null && (
        <RadarPanel
          radar={editingRadar === 'new' ? null : editingRadar}
          categories={categories}
          onClose={() => setEditingRadar(null)}
          onSaved={() => {
            loadRadars();
            loadDeals();
          }}
        />
      )}

      {editingAlert !== null && (
        <AlertPanel
          hunt={isHunt(editingAlert) ? editingAlert : null}
          suggestion={isSuggestion(editingAlert) ? editingAlert : null}
          categories={categories}
          radars={radars}
          onClose={() => setEditingAlert(null)}
          onSaved={loadHunts}
        />
      )}
    </div>
  );
}

// The editor takes either an existing alert or a starter to pre-fill from, and
// they are structurally similar enough that a discriminator is worth being
// explicit about. Only a saved alert has an id.
function isHunt(value: Hunt | 'new' | AlertSuggestion): value is Hunt {
  return value !== 'new' && 'matchTerms' in value;
}

function isSuggestion(value: Hunt | 'new' | AlertSuggestion): value is AlertSuggestion {
  return value !== 'new' && !('matchTerms' in value);
}

/**
 * What the alert is actually gated on, in as few words as fit the rail. The
 * per-unit ceiling leads because it is the one that decides whether the alert
 * ever fires, and it is the one nobody can recall from the name alone.
 */
function alertSubtitle(hunt: Hunt): string {
  const parts: string[] = [];
  if (hunt.maxUnitPriceCents !== null) {
    parts.push(`≤ ${money(hunt.maxUnitPriceCents)}/item`);
  }
  if (hunt.measurePresetCents !== null && hunt.measurePreset) {
    parts.push(`≤ ${money(hunt.measurePresetCents)} ${hunt.measurePreset.replace('per_', '/')}`);
  }
  if (hunt.minDiscountPct !== null) parts.push(`${hunt.minDiscountPct}%+ off`);
  if (!parts.length && hunt.maxPriceCents !== null) parts.push(`under ${money(hunt.maxPriceCents)}`);
  if (!parts.length) parts.push('any price');
  if (hunt.matchCount > 0) parts.push(`${hunt.matchCount} found`);
  if (!hunt.notify) parts.push('muted');
  if (!hunt.active) parts.push('off');
  return parts.join(' · ');
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
